/**
 * PreflightBanner.jsx
 *
 * Visual preflight gate. Shows a green / amber / red strip with
 * blockers, warnings, per-speaker clone health, and a rough credit
 * estimate. Read-only in the mockup — wired to live data in R3 when
 * `GET /api/voice-rendering/:bookId/preflight` lands.
 *
 * Prop shape matches mockRenderingStudio.getPreflight() output and is
 * already in the shape the future real endpoint will emit (see
 * PHASE_4D_SESSION_DIALOG_ABOUT_RENDER_STUDIO.md §Plan v3 — Pre-flight
 * detail). That way the component doesn't need to change when the
 * fixture is swapped for a thunk.
 */

import React, { useState } from 'react';
import { Icon, Label, Popup } from 'semantic-ui-react';
import { PREFLIGHT_STATUS } from './renderingConstants';
import '../../styles/chevronToggle.css';


const STATUS_COPY = {
  [PREFLIGHT_STATUS.GREEN]: {
    icon: 'check circle',
    title: 'Preflight green — ready to render',
    tone: 'Every speaker in this unit has an active voice clone.',
  },
  [PREFLIGHT_STATUS.AMBER]: {
    icon: 'exclamation circle',
    title: 'Preflight amber — render with caveats',
    tone: 'Some lines will use fallback presets. You can still render.',
  },
  [PREFLIGHT_STATUS.RED]: {
    icon: 'ban',
    title: 'Preflight red — render blocked',
    tone: 'Fix the blockers below before this unit can be rendered.',
  },
};

/**
 * ⭐ THE STRIP KEEPS ITS PLACE WHILE IT IS BEING FETCHED (2026-09-18).
 *
 * Returning null while loading meant the banner was ABSENT and then suddenly present, shoving the
 * action bar and the whole line table down the page a beat after the header drew. On a chapter that is
 * a flicker; on a Part or Whole Book, where the fetch takes about a second, it reads as the page
 * assembling itself crookedly. A placeholder of the same shape costs nothing and removes one layout
 * pass.
 *
 * ⚠️ It says WAITING, not green. A gate that looks like a verdict while holding no data is the mistake
 * this component spent the day being famous for; a confident-looking skeleton would be the same
 * mistake with better manners.
 */
const PreflightSkeleton = () => (
  <div className="preflight-banner s-loading">
    <div className="preflight-head">
      <Icon name="circle notch" size="large" loading />
      <div className="preflight-head-body">
        <div className="preflight-title">Checking this unit…</div>
        <div className="preflight-sub">Resolving every speaker against the current casting.</div>
      </div>
      <div className="preflight-budget">
        <div><strong>—</strong><span className="preflight-budget-label"> TTS calls</span></div>
        <div><strong>—</strong><span className="preflight-budget-label"> est. spend</span></div>
      </div>
    </div>
  </div>
);

const PreflightBanner = ({ preflight, onRefresh, loading = false }) => {
  /**
   * ⭐ COLLAPSED BY DEFAULT (operator, 2026-09-18, the day the preflight started carrying real data).
   * For its whole life this strip was empty — the endpoint shipped `{}` — so its detail cost nothing
   * to leave open. With the payload arriving it is 47 speaker chips and every blocker and warning in
   * the unit, above the table you are trying to read.
   *
   * ⚠️ THE VERDICT AND THE COUNTS STAY VISIBLE. Hiding the detail must never hide that there IS
   * detail: the head keeps the status, the spend, and a summary of what is folded away, so "collapsed"
   * can never read as "nothing to report" — which is the failure this component just spent a day
   * demonstrating.
   */
  const [open, setOpen] = useState(false);
  // Loading wins over absent: showing nothing is what caused the jump. A node with no preflight and no
  // fetch in flight (a non-executable unit) still renders nothing, as before.
  if (loading && !preflight) return <PreflightSkeleton />;
  if (!preflight) return null;
  const copy = STATUS_COPY[preflight.status] || STATUS_COPY[PREFLIGHT_STATUS.AMBER];
  const {
    status,
    blockers = [],
    warnings = [],
    speakers = [],
    // What KINDS of utterance this unit contains. Empty for every unit nobody has marked, which is
    // every unit until somebody does — so the strip below simply does not appear.
    kinds = [],
    // How much of this unit is SPOKEN DIFFERENTLY from how the book writes it, and how many
    // corrections are anchored to text that has changed and so are doing nothing. Null for every
    // unit with no corrections, which is every unit until somebody makes one.
    editorial = null,
    estTtsCalls = 0,
    estCreditsBurnedRoughly = 0,
  } = preflight;

  // What is behind the caret, named in the head so its absence is never mistaken for silence.
  const hidden = [
    blockers.length ? `${blockers.length} blocker${blockers.length === 1 ? '' : 's'}` : null,
    warnings.length ? `${warnings.length} warning${warnings.length === 1 ? '' : 's'}` : null,
    speakers.length ? `${speakers.length} speaker${speakers.length === 1 ? '' : 's'}` : null,
    kinds.length ? `${kinds.length} kind${kinds.length === 1 ? '' : 's'}` : null,
    editorial && editorial.lines ? `${editorial.lines} edited line${editorial.lines === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');
  const hasDetail = blockers.length + warnings.length + speakers.length + kinds.length > 0
    || !!(editorial && (editorial.lines || editorial.stale));

  return (
    <div className={`preflight-banner s-${status}`}>
      <div className="preflight-head">
        <Icon name={copy.icon} size="large" />
        <div className="preflight-head-body">
          <div className="preflight-title">{copy.title}</div>
          <div className="preflight-sub">
            {copy.tone}
            {hasDetail && (
              <span className="preflight-hidden-summary"> · {hidden}</span>
            )}
          </div>
        </div>
        {/*
          ⛔ THE RECHECK IS NOT OPTIONAL NOW THAT THIS IS CACHED. A preflight is a claim about casting,
          and casting is edited in other panels — a merge, a reassignment, a voice attached in the Green
          Room. The page keeps a per-selector cache so re-selecting a unit costs nothing, which means a
          reading can be older than the decision it describes. This is how you ask again.
        */}
        {onRefresh && (
          <button
            type="button"
            className="aai-chevron-toggle preflight-disclose"
            aria-label="Recheck this unit"
            title="Recheck — re-run the preflight against the current casting"
            onClick={onRefresh}
          >
            <Icon name="refresh" fitted />
          </button>
        )}
        {hasDetail && (
          /* The shared caret — a real <button> so a keyboard reaches it, and carrying the class that
             suppresses the starter template's global black focus ring on mouse click. */
          <button
            type="button"
            className="aai-chevron-toggle preflight-disclose"
            aria-expanded={open}
            aria-label={open ? 'Hide preflight detail' : 'Show preflight detail'}
            title={open ? 'Hide detail' : `Show detail — ${hidden}`}
            onClick={() => setOpen((v) => !v)}
          >
            <Icon name={open ? 'angle up' : 'angle down'} fitted />
          </button>
        )}
        <div className="preflight-budget">
          <div>
            <strong>{estTtsCalls.toLocaleString()}</strong>
            <span className="preflight-budget-label"> TTS calls</span>
          </div>
          <div>
            <strong>~${estCreditsBurnedRoughly.toFixed(2)}</strong>
            <span className="preflight-budget-label"> est. spend</span>
          </div>
        </div>
      </div>

      {open && blockers.length > 0 && (
        <ul className="preflight-list preflight-blockers">
          {blockers.map((b, i) => (
            <li key={i}>
              <Icon name="remove circle" /> {b}
            </li>
          ))}
        </ul>
      )}
      {open && warnings.length > 0 && (
        <ul className="preflight-list preflight-warnings">
          {warnings.map((w, i) => (
            <li key={i}>
              <Icon name="warning sign" /> {w}
            </li>
          ))}
        </ul>
      )}

      {/*
        ⭐ KINDS GET THEIR OWN CHIPS, beside the speakers, because they are the same question asked
        of the lines nobody speaks: is there a voice for this, yes or no. A kind with no voice is
        rendered amber and says so on the chip — the warning above names the count, the chip names
        the kind, and between them there is no way to read "marked" as "handled".
      */}
      {/*
        ⭐ WHAT IS NOT SPOKEN AS THE BOOK WRITES IT. An editorial correction leaves the text alone, so
        without a line here the panel would price and describe a render while saying nothing about the
        words having been changed. A stale correction is called out separately because it is doing
        NOTHING — the operator's to fix, and invisible unless said.
      */}
      {open && editorial && (editorial.lines > 0 || editorial.stale > 0) && (
        <div className="preflight-speakers preflight-editorial">
          <div>
            {editorial.lines > 0 && (
              <span>
                ✎ <strong>{editorial.lines}</strong> line{editorial.lines === 1 ? '' : 's'} spoken
                differently from the book ({editorial.occurrences} correction
                {editorial.occurrences === 1 ? '' : 's'}
                {editorial.rewrites > 0 ? `, ${editorial.rewrites} a rewrite` : ''})
              </span>
            )}
            {editorial.stale > 0 && (
              <div style={{ color: '#b32424' }}>
                ⚠️ {editorial.stale} correction{editorial.stale === 1 ? '' : 's'} anchored to text that
                has changed — not being applied. Re-scan and save in the Editorial corrections panel.
              </div>
            )}
          </div>
        </div>
      )}
      {open && kinds.length > 0 && (
        <div className="preflight-speakers preflight-kinds">
          {kinds.map((k) => (
            <Popup
              key={k.kind}
              size="mini"
              content={k.hasVoice
                ? `${k.lines} line(s) of this kind; ${k.voiced} voiced by it.`
                : `${k.lines} line(s) marked "${k.label}" with no voice — nothing will be rendered `
                  + 'for the ones nobody speaks.'}
              trigger={
                <Label className={`speaker-chip ${k.hasVoice ? 'is-green' : 'is-amber'}`}>
                  <Icon name={k.hasVoice ? 'tag' : 'warning sign'} />
                  {k.label}
                  <Label.Detail>{k.lines} lines</Label.Detail>
                </Label>
              }
            />
          ))}
        </div>
      )}

      {/*
        ⛔ THESE CHIPS WERE READING FIELDS THE SERVER DOES NOT SEND (fixed 2026-09-18). It reads
        `s.name`, `s.blockers` and `s.warnings`; `RenderUnitPreflight` returns `displayName`,
        `narratedForWantOfVoice` and `unresolvedCount`. So every chip rendered NAMELESS and always
        green, with a microphone icon even for a character that has no voice of its own — the exact
        fact the panel exists to show. `hasActiveClone` is no help either: the preflight sets it on any
        successful resolution, and a narrator fallback IS a success.

        ⭐ The distinction that matters is `narratedForWantOfVoice`: the character speaks in its own
        voice, or the Narrator is standing in for it. Identical audio, opposite meaning.
      */}
      {open && speakers.length > 0 && (
        <div className="preflight-speakers">
          {speakers.map((s) => {
            const name = s.displayName || s.name || '(unspoken)';
            const narrated = s.narratedForWantOfVoice || 0;
            const unresolved = s.unresolvedCount || 0;
            const cls = unresolved > 0 ? 'is-red' : narrated > 0 ? 'is-amber' : 'is-green';
            const popup = unresolved > 0
              ? `${unresolved} of ${s.lineCount} line(s) do not resolve — they will not be rendered.`
              : narrated > 0
                ? `No voice of its own — ${narrated} line(s) will be read by the Narrator.`
                : `Own voice · ${s.cloneVoiceId || 'voice id unavailable'}`;
            return (
              <Popup
                key={s.normName || name}
                content={popup}
                size="mini"
                trigger={
                  <Label className={`speaker-chip ${cls}`}>
                    <Icon name={narrated > 0 || unresolved > 0 ? 'microphone slash' : 'microphone'} />
                    {name}
                    <Label.Detail>{s.lineCount} lines</Label.Detail>
                  </Label>
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PreflightBanner;
